import React, { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./App.css";
import AuthModal from "./AuthModal.js";
import UserMenu from "./UserMenu.js";
import ResetPassword from "./ResetPassword.js";
import LoadingSpinner from "./LoadingSpinner.js";
import { BrowseGridSkeleton, HomePosterRowSkeleton, HomeTop10RowSkeleton } from "./PosterSkeletons.js";
import { supabase } from "./supabase_client.js";

import { API_BASE } from "./apiConfig";
import { optionalFiniteNumber } from "./releaseYear.js";
import { tmdbCardPosterUrl } from "./tmdbImage.js";
import {
  saveBrowseDetailReturn,
  readBrowseDetailReturn,
  hashPathForLocation,
  detailBackButtonLabel,
  saveRatingsPageReturn,
} from "./navigationReturn.js";
import { scrollMotionBehavior } from "./motionPreference.js";

const Browse = lazy(() => import("./Browse.js"));
const MediaDetail = lazy(() => import("./MediaDetail.js"));
const Top100 = lazy(() => import("./Top100.js"));
const UserProfile = lazy(() => import("./UserProfile.js"));
const Ratings = lazy(() => import("./Ratings.js"));
const Favorites = lazy(() => import("./Favorites.js"));
const Watchlist = lazy(() => import("./Watchlist.js"));
const Lists = lazy(() => import("./Lists.js"));
const Notifications = lazy(() => import("./Notifications.js"));
const Settings = lazy(() => import("./Settings.js"));
const AdminPanel = lazy(() => import("./AdminPanel.js"));
const Community = lazy(() => import("./Community.js"));
const PostDetail = lazy(() => import("./PostDetail.js"));

function normalizeTitleForMatch(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

async function enrichHomeWatchlistPosters(rows, userId) {
  if (!Array.isArray(rows) || rows.length === 0 || !userId) return rows || [];
  const api = String(API_BASE || "").trim();
  if (!api) return rows;
  const missing = rows.filter((r) => !r?.poster_path);
  if (missing.length === 0) return rows;

  const patch = new Map();
  for (const row of missing) {
    const key = `${row.type}-${row.tmdb_id}`;
    let posterPath = null;
    try {
      const res = await fetch(
        `${api}/api/tmdb/details?type=${encodeURIComponent(row.type)}&id=${encodeURIComponent(row.tmdb_id)}`
      );
      const d = await res.json();
      if (res.ok && d && typeof d === "object" && d.poster_path) {
        posterPath = d.poster_path;
      }
    } catch (_) {}

    if (!posterPath && row?.title) {
      try {
        const mediaType = row.type === "tv" ? "tv" : "movie";
        const res = await fetch(
          `${api}/api/tmdb/search?type=${encodeURIComponent(mediaType)}&query=${encodeURIComponent(row.title)}&page=1`
        );
        const d = await res.json();
        if (res.ok && Array.isArray(d?.results) && d.results.length > 0) {
          const target = normalizeTitleForMatch(row.title);
          const exact = d.results.find(
            (x) => normalizeTitleForMatch(x?.title || x?.name) === target && x?.poster_path
          );
          posterPath = exact?.poster_path || d.results.find((x) => x?.poster_path)?.poster_path || null;
        }
      } catch (_) {}
    }

    if (posterPath) {
      patch.set(key, posterPath);
      try {
        await supabase
          .from("watchlist")
          .update({ poster_path: posterPath })
          .eq("user_id", userId)
          .eq("tmdb_id", row.tmdb_id)
          .eq("type", row.type);
      } catch (_) {}
    }
  }

  if (!patch.size) return rows;
  return rows.map((row) => {
    const nextPoster = patch.get(`${row.type}-${row.tmdb_id}`);
    return nextPoster ? { ...row, poster_path: nextPoster } : row;
  });
}

function RouteFallback() {
  return (
    <div className="route-suspense-fallback" role="status" aria-live="polite">
      <LoadingSpinner size="md" label="Loading…" />
    </div>
  );
}

function getMainLandmarkLabel(page, ctx) {
  switch (page) {
    case "home":
      return "Home";
    case "browse":
      return ctx.browseDetail ? "Title details" : "Browse movies and series";
    case "top100":
      return ctx.top100Tab === "tv" ? "Top 100 series" : "Top 100 movies";
    case "profile":
      return "Your profile";
    case "user":
      return "Member profile";
    case "ratings":
      return "Your ratings";
    case "favorites":
      return "Your favorites";
    case "watchlist":
      return "Watchlist";
    case "suggestions":
      return "Suggestions for you";
    case "lists":
      return "Lists";
    case "notifications":
      return "Notifications";
    case "settings":
      return "Settings";
    case "resetPassword":
      return "Reset password";
    case "admin":
      return "Admin panel";
    case "community":
      return ctx.selectedPostId ? "Post thread" : "Community discussions";
    default:
      return "RateZone";
  }
}

const TMDB_MOVIE_GENRE_IDS = {
  Action: 28,
  Adventure: 12,
  Animation: 16,
  Comedy: 35,
  Crime: 80,
  Documentary: 99,
  Drama: 18,
  Family: 10751,
  Fantasy: 14,
  History: 36,
  Horror: 27,
  Music: 10402,
  Mystery: 9648,
  Romance: 10749,
  "Science Fiction": 878,
  "Sci-Fi": 878,
  Thriller: 53,
  War: 10752,
  Western: 37,
};

function parsePostLocationHash(hash) {
  if (!hash.startsWith("#/post/")) {
    return { postId: null, focusCommentId: null };
  }
  const rest = hash.slice("#/post/".length);
  const [pathPart, queryPart] = rest.split("?");
  const rawPost = (pathPart ? pathPart.split("#")[0] : "").trim() || "";
  let postId = null;
  if (rawPost) {
    try {
      postId = decodeURIComponent(rawPost.replace(/\+/g, " "));
    } catch {
      postId = rawPost;
    }
  }
  let focusCommentId = null;
  if (queryPart) {
    const qOnly = queryPart.split("#")[0] || queryPart;
    const params = new URLSearchParams(qOnly);
    const raw = params.get("comment") || params.get("c");
    if (raw) {
      try {
        focusCommentId = decodeURIComponent(raw.trim());
      } catch {
        focusCommentId = raw.trim();
      }
    }
  }
  return { postId, focusCommentId };
}

function normalizeSearchItem(item, type) {
  const title = item?.title || item?.name || "Untitled";
  const mediaType = (item?.media_type === "tv" || item?.media_type === "movie") ? item.media_type : (type === "tv" ? "tv" : "movie");
  const typeLabel = mediaType === "tv" ? "Series" : "Movie";
  const year = (item?.release_date || item?.first_air_date || "").slice(0, 4);
  return { id: item?.id, mediaType, title, typeLabel, year };
}

function App() {
  const [currentPage, setCurrentPage] = useState("home");
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [user, setUser] = useState(null);
  const [userAvatar, setUserAvatar] = useState(null);
  const [userIsAdmin, setUserIsAdmin] = useState(false);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [browseSearchQuery, setBrowseSearchQuery] = useState("");
  const [navbarSuggestions, setNavbarSuggestions] = useState([]);
  const [navbarSuggestionsLoading, setNavbarSuggestionsLoading] = useState(false);
  const [navbarShowSuggestions, setNavbarShowSuggestions] = useState(false);
  const navbarSuggestionsDebounceRef = useRef(null);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [postFocusCommentId, setPostFocusCommentId] = useState(null);
  const [browseDetail, setBrowseDetail] = useState(null);
  const [viewedUserId, setViewedUserId] = useState(null);
  const [browseMenuCategory, setBrowseMenuCategory] = useState(null);
  const [top100Tab, setTop100Tab] = useState("movie");
  const [homeTrending, setHomeTrending] = useState([]);
  const [homeTrendingLoading, setHomeTrendingLoading] = useState(false);
  const [homeWatchlist, setHomeWatchlist] = useState([]);
  const [homeWatchlistLoading, setHomeWatchlistLoading] = useState(false);
  const [homeRatings, setHomeRatings] = useState([]);
  const [homeRatingsLoading, setHomeRatingsLoading] = useState(false);
  const [homeSuggestions, setHomeSuggestions] = useState([]);
  const [homeSuggestionsLoading, setHomeSuggestionsLoading] = useState(false);
  const [homeSuggestionsGenreName, setHomeSuggestionsGenreName] = useState(null);
  const [homeTop10Today, setHomeTop10Today] = useState([]);
  const [homeTop10TodayLoading, setHomeTop10TodayLoading] = useState(false);
  const homeTrendingRowRef = useRef(null);
  const homeRatingsRowRef = useRef(null);
  const homeWatchlistRowRef = useRef(null);
  const homeSuggestionsRowRef = useRef(null);
  const homeTop10RowRef = useRef(null);
  const prevLocationHashRef = useRef(typeof window !== "undefined" ? window.location.hash : "");
  const passwordRecoveryUiLockRef = useRef(false);

  const scrollStep = 176;
  const scrollRow = useCallback((ref, direction) => {
    const el = ref?.current;
    if (!el) return;
    const delta = direction === "left" ? -scrollStep : scrollStep;
    el.scrollBy({ left: delta, behavior: scrollMotionBehavior() });
  }, []);

  const scrollTrending = useCallback((direction) => scrollRow(homeTrendingRowRef, direction), [scrollRow]);
  const scrollRatings = useCallback((direction) => scrollRow(homeRatingsRowRef, direction), [scrollRow]);
  const scrollWatchlist = useCallback((direction) => scrollRow(homeWatchlistRowRef, direction), [scrollRow]);
  const scrollSuggestions = useCallback((direction) => scrollRow(homeSuggestionsRowRef, direction), [scrollRow]);
  const scrollTop10 = useCallback((direction) => scrollRow(homeTop10RowRef, direction), [scrollRow]);

  const navigateFromProfile = useCallback((page) => {
    setCurrentPage(page);
    window.location.hash = page === "home" ? "" : `/${page}`;
  }, []);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash || "";
    if (/type=recovery/i.test(hash)) {
      passwordRecoveryUiLockRef.current = true;
      setCurrentPage("resetPassword");
      setSelectedPostId(null);
      setBrowseDetail(null);
      setViewedUserId(null);
      setBrowseSearchQuery("");
      setBrowseMenuCategory(null);
    }
  }, []);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    const search = window.location.search || "";
    const params = new URLSearchParams(search);
    if (!params.get("code")) return;
    const isRecovery = (params.get("type") || "").toLowerCase() === "recovery";
    if (!isRecovery) return;

    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.auth.exchangeCodeForSession(window.location.href);
      if (cancelled || error) return;
      if (!data?.session?.user) return;
      const path = window.location.pathname || "/";
      window.history.replaceState({}, document.title, `${path}#/reset-password`);
      prevLocationHashRef.current = "#/reset-password";
      passwordRecoveryUiLockRef.current = true;
      setCurrentPage("resetPassword");
      setSelectedPostId(null);
      setBrowseDetail(null);
      setViewedUserId(null);
      setBrowseSearchQuery("");
      setBrowseMenuCategory(null);
      setUser(data.session.user);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      const prevHash = prevLocationHashRef.current;
      setPostFocusCommentId(null);
      const search = typeof window !== "undefined" ? window.location.search || "" : "";
      const params = new URLSearchParams(search);
      if (params.get("code") && (params.get("type") || "").toLowerCase() === "recovery") {
        prevLocationHashRef.current = hash;
        return;
      }
      if (/type=recovery/i.test(hash)) {
        passwordRecoveryUiLockRef.current = true;
        setCurrentPage("resetPassword");
        setSelectedPostId(null);
        setBrowseDetail(null);
        setViewedUserId(null);
        setBrowseSearchQuery("");
        setBrowseMenuCategory(null);
        prevLocationHashRef.current = hash;
        return;
      }
      if (
        (hash.startsWith("#/browse/movie/") || hash.startsWith("#/browse/tv/")) &&
        prevHash !== hash
      ) {
        saveBrowseDetailReturn(prevHash);
      }
      if (hash === "#/ratings" && prevHash !== hash) {
        saveRatingsPageReturn(prevHash);
      }
      if (hash.startsWith("#/user/")) {
        passwordRecoveryUiLockRef.current = false;
        const id = hash.replace("#/user/", "").split("/")[0];
        setViewedUserId(id || null);
        setCurrentPage(id ? "user" : "community");
        setSelectedPostId(null);
        setBrowseSearchQuery("");
      } else if (hash.startsWith("#/post/")) {
        passwordRecoveryUiLockRef.current = false;
        const { postId, focusCommentId } = parsePostLocationHash(hash);
        setSelectedPostId(postId);
        setPostFocusCommentId(focusCommentId);
        setCurrentPage("community");
        setViewedUserId(null);
        setBrowseDetail(null);
      } else if (hash.startsWith("#/browse/movie/")) {
        passwordRecoveryUiLockRef.current = false;
        const id = hash.replace("#/browse/movie/", "");
        setBrowseDetail({ type: "movie", id });
        setCurrentPage("browse");
        setSelectedPostId(null);
      } else if (hash.startsWith("#/browse/tv/")) {
        passwordRecoveryUiLockRef.current = false;
        const id = hash.replace("#/browse/tv/", "");
        setBrowseDetail({ type: "tv", id });
        setCurrentPage("browse");
        setSelectedPostId(null);
      } else if (hash === "#/top100" || hash === "#/top100/movie" || hash === "#/top100/tv") {
        passwordRecoveryUiLockRef.current = false;
        setCurrentPage("top100");
        setTop100Tab(hash === "#/top100/tv" ? "tv" : "movie");
        setViewedUserId(null);
        setSelectedPostId(null);
        setBrowseDetail(null);
        setBrowseSearchQuery("");
        setBrowseMenuCategory(null);
      } else if (hash === "#/admin") {
        passwordRecoveryUiLockRef.current = false;
        setCurrentPage("admin");
        setViewedUserId(null);
        setSelectedPostId(null);
        setBrowseDetail(null);
        setBrowseSearchQuery("");
        setBrowseMenuCategory(null);
      } else if (hash === "#/lists" || hash.startsWith("#/lists/")) {
        passwordRecoveryUiLockRef.current = false;
        setCurrentPage("lists");
        setViewedUserId(null);
        setSelectedPostId(null);
        setBrowseDetail(null);
        setBrowseSearchQuery("");
        setBrowseMenuCategory(null);
      } else if (hash === "#/suggestions") {
        passwordRecoveryUiLockRef.current = false;
        setCurrentPage("suggestions");
        setViewedUserId(null);
        setSelectedPostId(null);
        setBrowseDetail(null);
        setBrowseSearchQuery("");
        setBrowseMenuCategory(null);
      } else if (hash === "#/community" || hash === "#/browse" || hash === "#/profile" || hash === "#/ratings" || hash === "#/favorites" || hash === "#/watchlist" || hash === "#/notifications" || hash === "#/settings") {
        passwordRecoveryUiLockRef.current = false;
        setCurrentPage(hash.replace("#/", ""));
        setViewedUserId(null);
        setSelectedPostId(null);
        if (hash === "#/browse") setBrowseDetail(null);
        else {
          setBrowseSearchQuery("");
          setBrowseMenuCategory(null);
        }
      } else if (hash === "#/reset-password") {
        setCurrentPage("resetPassword");
        setViewedUserId(null);
        setSelectedPostId(null);
        setBrowseDetail(null);
        setBrowseSearchQuery("");
        setBrowseMenuCategory(null);
      } else if (hash === "" || hash === "#" || hash === "#/") {
        if (passwordRecoveryUiLockRef.current) {
          window.location.hash = "#/reset-password";
          setCurrentPage("resetPassword");
          prevLocationHashRef.current = "#/reset-password";
          return;
        }
        passwordRecoveryUiLockRef.current = false;
        setCurrentPage("home");
        setSelectedPostId(null);
        setBrowseDetail(null);
        setViewedUserId(null);
        setBrowseSearchQuery("");
        setBrowseMenuCategory(null);
      }
      prevLocationHashRef.current = hash;
    };

    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserAvatar(session.user.id);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") {
        passwordRecoveryUiLockRef.current = true;
        setCurrentPage("resetPassword");
        setSelectedPostId(null);
        setBrowseDetail(null);
        setViewedUserId(null);
        setBrowseSearchQuery("");
        setBrowseMenuCategory(null);
        window.location.hash = "#/reset-password";
        prevLocationHashRef.current = "#/reset-password";
      }
      if (event === "SIGNED_OUT") {
        passwordRecoveryUiLockRef.current = false;
      }
      if (event === "SIGNED_IN" && session?.user) {
        const hash = window.location.hash || "";
        const search = window.location.search || "";
        const signupInHash = /type=signup/i.test(hash);
        const signupInSearch = (new URLSearchParams(search).get("type") || "").toLowerCase() === "signup";
        if (signupInHash || signupInSearch) {
          passwordRecoveryUiLockRef.current = false;
          setCurrentPage("profile");
          setSelectedPostId(null);
          setBrowseDetail(null);
          setViewedUserId(null);
          setBrowseSearchQuery("");
          setBrowseMenuCategory(null);
          window.location.hash = "#/profile";
          prevLocationHashRef.current = "#/profile";
        }
      }
      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserAvatar(session.user.id);
      } else {
        setUserAvatar(null);
        setUserIsAdmin(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (currentPage === "profile" && user) {
      loadUserAvatar(user.id);
    }
  }, [currentPage, user]);

  useEffect(() => {
    const handleProfileUpdate = () => {
      if (user) {
        loadUserAvatar(user.id);
      }
    };
    window.addEventListener("profileUpdated", handleProfileUpdate);
    return () => window.removeEventListener("profileUpdated", handleProfileUpdate);
  }, [user]);

  useEffect(() => {
    if (user) return;
    setHomeTrendingLoading(true);
    fetch(`${API_BASE}/api/tmdb/trending?type=movie&time=week`)
      .then((res) => res.json())
      .then((data) => {
        const results = Array.isArray(data?.results) ? data.results : [];
        setHomeTrending(results.slice(0, 20).map((r) => ({
          id: r.id,
          mediaType: "movie",
          title: r.title || "Untitled",
          posterPath: r.poster_path || null,
        })));
      })
      .catch(() => setHomeTrending([]))
      .finally(() => setHomeTrendingLoading(false));
  }, [user]);

  useEffect(() => {
    if (!user?.id) {
      setHomeWatchlist([]);
      return;
    }
    const loadHomeWatchlist = () => {
      setHomeWatchlistLoading(true);
      supabase
        .from("watchlist")
        .select("tmdb_id, type, title, poster_path, release_date, vote_average, vote_count")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(20)
        .then(async ({ data, error }) => {
          if (!error && data) {
            const withPosters = await enrichHomeWatchlistPosters(data, user.id);
            setHomeWatchlist(withPosters);
          } else setHomeWatchlist([]);
        })
        .finally(() => setHomeWatchlistLoading(false));
    };
    loadHomeWatchlist();
    window.addEventListener("watchlistUpdated", loadHomeWatchlist);
    return () => window.removeEventListener("watchlistUpdated", loadHomeWatchlist);
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) {
      setHomeRatings([]);
      return;
    }
    setHomeRatingsLoading(true);
    supabase
      .from("user_ratings")
      .select("tmdb_id, type, title, poster_path, rating")
      .eq("user_id", user.id)
      .order("updated_at", { ascending: false })
      .limit(20)
      .then(({ data, error }) => {
        if (!error && data) setHomeRatings(data);
        else setHomeRatings([]);
      })
      .finally(() => setHomeRatingsLoading(false));
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) {
      setHomeSuggestions([]);
      setHomeSuggestionsGenreName(null);
      return;
    }
    let cancelled = false;
    setHomeSuggestionsLoading(true);
    supabase
      .from("user_ratings")
      .select("genres")
      .eq("user_id", user.id)
      .then(({ data, error }) => {
        if (cancelled || error || !data?.length) {
          if (!cancelled) {
            setHomeSuggestions([]);
            setHomeSuggestionsGenreName(null);
          }
          setHomeSuggestionsLoading(false);
          return;
        }
        const genreCounts = {};
        data.forEach((r) => {
          if (r.genres && typeof r.genres === "string") {
            r.genres.split(",").map((g) => g.trim()).filter(Boolean).forEach((genre) => {
              genreCounts[genre] = (genreCounts[genre] || 0) + 1;
            });
          }
        });
        const favoriteGenre = Object.keys(genreCounts).length > 0
          ? Object.entries(genreCounts).sort((a, b) => b[1] - a[1])[0][0]
          : null;
        const genreId = favoriteGenre ? TMDB_MOVIE_GENRE_IDS[favoriteGenre] : null;
        if (!genreId) {
          setHomeSuggestions([]);
          setHomeSuggestionsGenreName(null);
          setHomeSuggestionsLoading(false);
          return;
        }
        Promise.all([
          fetch(`${API_BASE}/api/tmdb/discover?type=movie&with_genres=${genreId}&with_original_language=en&sort_by=popularity.desc&min_votes=300&min_rating=5&page=1`).then((res) => res.json()),
          fetch(`${API_BASE}/api/tmdb/discover?type=movie&with_genres=${genreId}&with_original_language=en&sort_by=popularity.desc&min_votes=300&min_rating=5&page=2`).then((res) => res.json()),
          fetch(`${API_BASE}/api/tmdb/discover?type=movie&with_genres=${genreId}&with_original_language=en&sort_by=popularity.desc&min_votes=300&min_rating=5&page=3`).then((res) => res.json()),
        ])
          .then(([disc1, disc2, disc3]) => {
            if (cancelled) return;
            const r1 = Array.isArray(disc1?.results) ? disc1.results : [];
            const r2 = Array.isArray(disc2?.results) ? disc2.results : [];
            const r3 = Array.isArray(disc3?.results) ? disc3.results : [];
            const combined = [...r1, ...r2, ...r3];
            const seen = new Set();
            const deduped = combined
              .filter((r) => {
                if (seen.has(r.id)) return false;
                seen.add(r.id);
                return true;
              })
              .filter(
                (r) =>
                  !!r.poster_path &&
                  String(r.original_language || "").toLowerCase() === "en" &&
                  (r.vote_count || 0) >= 300 &&
                  (r.vote_average || 0) >= 5
              );
            if (!cancelled) {
              setHomeSuggestionsGenreName(favoriteGenre);
              setHomeSuggestions(deduped.slice(0, 20).map((r) => ({
                id: r.id,
                mediaType: "movie",
                title: r.title || "Untitled",
                posterPath: r.poster_path || null,
                year: (r.release_date || "").slice(0, 4) || null,
                rating: optionalFiniteNumber(r.vote_average),
              })));
            }
          })
          .catch(() => {
            if (!cancelled) {
              setHomeSuggestions([]);
              setHomeSuggestionsGenreName(null);
            }
          })
          .finally(() => { if (!cancelled) setHomeSuggestionsLoading(false); });
      });
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) {
      setHomeTop10Today([]);
      return;
    }
    let cancelled = false;
    setHomeTop10TodayLoading(true);
    fetch(`${API_BASE}/api/tmdb/trending?type=all&time=day&page=1`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        const results = Array.isArray(data?.results) ? data.results : [];
        const withPoster = results.filter((r) => r?.poster_path);
        setHomeTop10Today(
          withPoster.slice(0, 10).map((r) => {
            const mediaType = r.media_type === "tv" ? "tv" : "movie";
            const title =
              mediaType === "tv"
                ? r.name || "Untitled"
                : r.title || "Untitled";
            return {
              id: r.id,
              mediaType,
              title,
              posterPath: r.poster_path || null,
            };
          })
        );
      })
      .catch(() => {
        if (!cancelled) setHomeTop10Today([]);
      })
      .finally(() => {
        if (!cancelled) setHomeTop10TodayLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    const q = (searchQuery || "").trim();
    if (q.length < 2) {
      setNavbarSuggestions([]);
      setNavbarShowSuggestions(false);
      if (navbarSuggestionsDebounceRef.current) {
        clearTimeout(navbarSuggestionsDebounceRef.current);
        navbarSuggestionsDebounceRef.current = null;
      }
      return;
    }
    if (navbarSuggestionsDebounceRef.current) clearTimeout(navbarSuggestionsDebounceRef.current);
    navbarSuggestionsDebounceRef.current = setTimeout(async () => {
      setNavbarSuggestionsLoading(true);
      try {
        const res = await fetch(
          `${API_BASE}/api/tmdb/search?query=${encodeURIComponent(q)}&type=all&page=1`
        );
        const data = await res.json();
        if (res.ok && Array.isArray(data?.results)) {
          const type = (t) => (t === "tv" || t === "movie" ? t : "movie");
          setNavbarSuggestions(
            data.results.slice(0, 8).map((item) => normalizeSearchItem(item, type(item.media_type)))
          );
          setNavbarShowSuggestions(true);
        } else {
          setNavbarSuggestions([]);
        }
      } catch (_e) {
        setNavbarSuggestions([]);
      } finally {
        setNavbarSuggestionsLoading(false);
      }
    }, 300);
    return () => {
      if (navbarSuggestionsDebounceRef.current) clearTimeout(navbarSuggestionsDebounceRef.current);
    };
  }, [searchQuery]);

  const handleNavbarSuggestionSelect = useCallback((item) => {
    setSearchQuery("");
    setNavbarShowSuggestions(false);
    setNavbarSuggestions([]);
    saveBrowseDetailReturn(window.location.hash);
    setCurrentPage("browse");
    setBrowseDetail({ type: item.mediaType, id: String(item.id) });
    window.location.hash = `#/browse/${item.mediaType}/${item.id}`;
  }, []);

  const loadUnreadNotificationsCount = useCallback(async (userId) => {
    try {
      const { count, error } = await supabase
        .from("notifications")
        .select("*", { count: "exact", head: true })
        .eq("user_id", userId)
        .eq("read", false);
      if (!error) setUnreadNotificationsCount(count ?? 0);
      else setUnreadNotificationsCount(0);
    } catch {
      setUnreadNotificationsCount(0);
    }
  }, []);

  const handleNotificationsMarkRead = useCallback(() => {
    if (user?.id) loadUnreadNotificationsCount(user.id);
  }, [user?.id, loadUnreadNotificationsCount]);

  useEffect(() => {
    if (user?.id) loadUnreadNotificationsCount(user.id);
    else setUnreadNotificationsCount(0);
  }, [user?.id, loadUnreadNotificationsCount]);

  const loadUserAvatar = async (userId) => {
    try {
      const [{ data: avData, error: avErr }, { data: adminData, error: adminErr }] = await Promise.all([
        supabase.from("profiles").select("avatar_url").eq("user_id", userId).single(),
        supabase.from("profiles").select("is_admin").eq("user_id", userId).single(),
      ]);

      if (!avErr && avData) {
        setUserAvatar(avData.avatar_url || null);
      } else {
        setUserAvatar(null);
      }

      if (!adminErr && adminData) {
        setUserIsAdmin(!!adminData.is_admin);
      } else {
        setUserIsAdmin(false);
      }
    } catch (error) {
      setUserAvatar(null);
      setUserIsAdmin(false);
    }
  };

  const mainNavBrowseList = currentPage === "browse" && !browseDetail;
  const mainNavMoviesActive = mainNavBrowseList && browseMenuCategory === "movies";
  const mainNavSeriesActive = mainNavBrowseList && browseMenuCategory === "series";
  const mainNavCommunityActive = currentPage === "community";
  const mainNavTop100Active = currentPage === "top100";

  const mainLandmarkLabel = useMemo(
    () =>
      getMainLandmarkLabel(currentPage, {
        browseDetail,
        top100Tab,
        selectedPostId,
      }),
    [currentPage, browseDetail, top100Tab, selectedPostId]
  );

  return (
    <div className="page">
      <a
        href="#rz-main-content"
        className="skip-link"
        onClick={(e) => {
          e.preventDefault();
          const el = document.getElementById("rz-main-content");
          if (el) {
            el.focus();
          }
        }}
      >
        Skip to main content
      </a>
      <nav className="navbar" aria-label="Primary">
        <div className="navbar_start">
          <div className="navbar_title" onClick={() => {
            passwordRecoveryUiLockRef.current = false;
            setCurrentPage("home");
            window.location.hash = "";
          }}>RateZone</div>
          <div className="navbar_links">
            <a
              href="#/browse"
              className={`navbar_link ${mainNavMoviesActive ? "navbar_link_active" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                try {
                  sessionStorage.setItem("browseSearchCategory", "movies");
                } catch (_) {}
                setBrowseMenuCategory("movies");
                setCurrentPage("browse");
                window.location.hash = "#/browse";
              }}
            >
              Movies
            </a>
            <a
              href="#/browse"
              className={`navbar_link ${mainNavSeriesActive ? "navbar_link_active" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                try {
                  sessionStorage.setItem("browseSearchCategory", "series");
                } catch (_) {}
                setBrowseMenuCategory("series");
                setCurrentPage("browse");
                window.location.hash = "#/browse";
              }}
            >
              Series
            </a>
            <a
              href="#/community"
              className={`navbar_link ${mainNavCommunityActive ? "navbar_link_active" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                setCurrentPage("community");
                window.location.hash = "#/community";
              }}
            >
              Community
            </a>
            <a
              href="#/top100/movie"
              className={`navbar_link ${mainNavTop100Active ? "navbar_link_active" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                setTop100Tab("movie");
                setBrowseMenuCategory(null);
                setCurrentPage("top100");
                window.location.hash = "#/top100/movie";
              }}
            >
              Top 100
            </a>
          </div>
        </div>
        <div className="navbar_actions">
          <div className="navbar_search_wrap">
            <form className="navbar_search_form" onSubmit={(e) => {
              e.preventDefault();
              const q = searchQuery.trim();
              if (q) {
                setBrowseSearchQuery(q);
                setNavbarShowSuggestions(false);
                setNavbarSuggestions([]);
                setCurrentPage("browse");
                window.location.hash = "/browse";
              }
            }}>
              <input
                type="text"
                className="navbar_search_input"
                placeholder="Search movies, series..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchQuery.trim().length >= 2 && setNavbarShowSuggestions(true)}
                onBlur={() => setTimeout(() => setNavbarShowSuggestions(false), 200)}
                autoComplete="off"
              />
              <button type="submit" className="navbar_search_button" title="Search">
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="m19 19-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </form>
            {navbarShowSuggestions && (navbarSuggestions.length > 0 || navbarSuggestionsLoading) && (
              <div className="navbar_suggestions_dropdown">
                {navbarSuggestionsLoading ? (
                  <div className="navbar_suggestions_item navbar_suggestions_loading">
                    <LoadingSpinner layout="row" size="sm" label="Loading…" />
                  </div>
                ) : (
                  navbarSuggestions.map((item) => (
                    <button
                      key={`${item.mediaType}-${item.id}`}
                      type="button"
                      className="navbar_suggestions_item"
                      onMouseDown={(e) => { e.preventDefault(); handleNavbarSuggestionSelect(item); }}
                    >
                      <span className="navbar_suggestions_title">{item.title}</span>
                      <span className="navbar_suggestions_meta">{item.typeLabel}{item.year ? ` · ${item.year}` : ""}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          {user ? (
            <UserMenu
              userAvatar={userAvatar}
              isAdmin={userIsAdmin}
              unreadNotificationsCount={unreadNotificationsCount}
              onNavigate={(page) => {
                setCurrentPage(page);
                window.location.hash = page === "home" ? "" : `/${page}`;
              }}
              onSignOut={async () => {
                await supabase.auth.signOut();
                setUserIsAdmin(false);
                setCurrentPage("home");
                window.location.hash = "";
              }}
            />
          ) : (
            <button className="navbar_login" onClick={() => setShowAuthModal(true)}>
              Sign In
            </button>
          )}
        </div>
      </nav>

      <main id="rz-main-content" className="content" tabIndex={-1} aria-label={mainLandmarkLabel}>
        {currentPage === "home" && (
          <>
            <div className="hero-section">
              <div className="hero-content">
                <h1 className="hero-title">
                  Your Ultimate<br />
                  <span className="hero-title-accent">Movie & Series Hub</span>
                </h1>
                <p className="hero-description">
                  Rate, review, and discover your favorite content. Never forget what you've watched or what to watch next.
                </p>
                <div className="hero-buttons">
                  {user ? (
                    <button className="hero-button-primary" onClick={() => {
                      setCurrentPage("profile");
                      window.location.hash = "/profile";
                    }}>
                      My Library
                    </button>
                  ) : (
                    <button className="hero-button-primary" onClick={() => setShowAuthModal(true)}>
                      Get Started
                    </button>
                  )}
                  <button className="hero-button-secondary" onClick={() => {
                    setCurrentPage("browse");
                    window.location.hash = "/browse";
                  }}>Browse Content</button>
                  <button className="hero-button-secondary" onClick={() => {
                    setCurrentPage("community");
                    window.location.hash = "/community";
                  }}>Community</button>
                </div>
              </div>
            </div>

            {!user && (
            <div className="why-section">
              <h2 className="why-title">Why RateZone?</h2>
              <p className="why-subtitle">Everything you need to track your viewing journey</p>
              <div className="feature-cards">
                <div className="feature-card">
                  <div className="feature-icon feature-icon-rating" aria-hidden>
                    {[...Array(10)].map((_, idx) => (
                      <span key={idx} className="feature-star">★</span>
                    ))}
                  </div>
                  <h3 className="feature-title">Rate & Review</h3>
                  <p className="feature-description">Give ratings from 0-10 and write detailed reviews to share your thoughts with the community.</p>
                </div>
                <div className="feature-card">
                  <div className="feature-icon feature-icon-library" aria-hidden>
                    <span className="feature-stack feature-stack-1" />
                    <span className="feature-stack feature-stack-2" />
                    <span className="feature-stack feature-stack-3" />
                  </div>
                  <h3 className="feature-title">Personal Library</h3>
                  <p className="feature-description">Keep track of all movies and series you've watched in one organized place, sorted your way.</p>
                </div>
                <div className="feature-card">
                  <div className="feature-icon feature-icon-community" aria-hidden>
                    <span className="feature-chat feature-chat-1" />
                    <span className="feature-chat feature-chat-2" />
                    <span className="feature-chat feature-chat-3" />
                  </div>
                  <h3 className="feature-title">Community Discussions</h3>
                  <p className="feature-description">Engage in discussions with other users through threaded comments on reviews.</p>
                </div>
              </div>
            </div>
            )}

            {!user ? (
              <div className="home-content-section">
                <h2 className="home-content-section-title">Trending movies</h2>
                {homeTrendingLoading ? (
                  <HomePosterRowSkeleton count={8} textLines={1} />
                ) : homeTrending.length === 0 ? (
                  <p className="home-content-placeholder">No trending data right now.</p>
                ) : (
                  <div className="home-poster-row-with-arrows">
                    {homeTrending.length >= 9 && (
                      <button type="button" className="home-poster-arrow home-poster-arrow-left" onClick={() => scrollTrending("left")} aria-label="Scroll left">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
                      </button>
                    )}
                    <div className="home-poster-row" ref={homeTrendingRowRef}>
                      {homeTrending.map((item) => (
                        <a
                          key={item.id}
                          href={`#/browse/${item.mediaType}/${item.id}`}
                          className="home-poster-card"
                          onClick={() => {
                            setCurrentPage("browse");
                            setBrowseDetail({ type: item.mediaType, id: String(item.id) });
                          }}
                        >
                          <div className="home-poster-card-poster">
                            {item.posterPath ? (
                              <img src={tmdbCardPosterUrl(item.posterPath)} alt={item.title} loading="lazy" decoding="async" />
                            ) : (
                              <div className="home-poster-card-placeholder">🎬</div>
                            )}
                          </div>
                          <span className="home-poster-card-title">{item.title}</span>
                        </a>
                      ))}
                    </div>
                    {homeTrending.length >= 9 && (
                      <button type="button" className="home-poster-arrow home-poster-arrow-right" onClick={() => scrollTrending("right")} aria-label="Scroll right">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <>
                {(homeTop10TodayLoading || homeTop10Today.length > 0) && (
                  <div className="home-content-section home-top10-section">
                    <h2 className="home-content-section-title home-top10-section-title">
                      <span className="home-top10-section-title-prefix">{"Today's Top "}</span>
                      <span className="home-top10-section-title-numeral">10</span>
                    </h2>
                    {homeTop10TodayLoading ? (
                      <HomeTop10RowSkeleton count={10} />
                    ) : homeTop10Today.length === 0 ? null : (
                      <div className="home-poster-row-with-arrows">
                        <button
                          type="button"
                          className="home-poster-arrow home-poster-arrow-left"
                          onClick={() => scrollTop10("left")}
                          aria-label="Scroll top 10 left"
                        >
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
                        </button>
                        <div className="home-poster-row home-top10-row" ref={homeTop10RowRef}>
                          {homeTop10Today.map((item, index) => (
                            <div key={`${item.mediaType}-${item.id}`} className="home-top10-slot">
                              <span
                                className={`home-top10-rank${index === 0 ? " home-top10-rank--first" : ""}${index + 1 >= 10 ? " home-top10-rank--twodigit" : ""}`}
                                aria-hidden
                              >
                                {index + 1}
                              </span>
                              <a
                                href={`#/browse/${item.mediaType}/${item.id}`}
                                className="home-top10-card"
                                title={item.title}
                                aria-label={`${index + 1}. ${item.title}, open details`}
                                onClick={() => {
                                  setCurrentPage("browse");
                                  setBrowseDetail({ type: item.mediaType, id: String(item.id) });
                                }}
                              >
                                <div className="home-top10-poster">
                                  {item.posterPath ? (
                                    <img src={tmdbCardPosterUrl(item.posterPath)} alt="" loading="lazy" decoding="async" />
                                  ) : (
                                    <div className="home-poster-card-placeholder">🎬</div>
                                  )}
                                </div>
                                <span className="home-top10-card-title">{item.title}</span>
                              </a>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          className="home-poster-arrow home-poster-arrow-right"
                          onClick={() => scrollTop10("right")}
                          aria-label="Scroll top 10 right"
                        >
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
                        </button>
                      </div>
                    )}
                  </div>
                )}
                <div className="home-content-section home-library-section">
                  <h2 className="home-content-section-title">Suggestions for you</h2>
                  {homeSuggestionsLoading ? (
                    <HomePosterRowSkeleton count={8} textLines={2} />
                  ) : homeSuggestions.length === 0 ? (
                    <p className="home-content-placeholder">Rate more titles to get suggestions based on your favorite genre.</p>
                  ) : (
                    <div className="home-poster-row-with-arrows">
                      {homeSuggestions.length >= 9 && (
                        <button type="button" className="home-poster-arrow home-poster-arrow-left" onClick={() => scrollSuggestions("left")} aria-label="Scroll left">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
                        </button>
                      )}
                      <div className="home-poster-row" ref={homeSuggestionsRowRef}>
                        {homeSuggestions.map((item) => (
                          <a
                            key={`${item.mediaType}-${item.id}`}
                            href={`#/browse/${item.mediaType}/${item.id}`}
                            className="home-poster-card"
                            onClick={() => {
                              setCurrentPage("browse");
                              setBrowseDetail({ type: item.mediaType, id: String(item.id) });
                            }}
                          >
                            <div className="home-poster-card-poster">
                              {item.posterPath ? (
                                <img src={tmdbCardPosterUrl(item.posterPath)} alt={item.title} loading="lazy" decoding="async" />
                              ) : (
                                <div className="home-poster-card-placeholder">🎬</div>
                              )}
                            </div>
                            <span className="home-poster-card-title">{item.title}</span>
                            <span className="home-poster-card-subtitle">Movie</span>
                          </a>
                        ))}
                      </div>
                      {homeSuggestions.length >= 9 && (
                        <button type="button" className="home-poster-arrow home-poster-arrow-right" onClick={() => scrollSuggestions("right")} aria-label="Scroll right">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
                        </button>
                      )}
                    </div>
                  )}
                  <div className="home-section-action-wrap">
                    <button
                      type="button"
                      className="home-section-action-btn"
                      onClick={() => {
                        setCurrentPage("suggestions");
                        window.location.hash = "#/suggestions";
                      }}
                    >
                      <span>View all suggestions</span>
                      <svg className="home-section-action-btn-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="home-content-section home-library-section">
                  <h2 className="home-content-section-title">Your ratings</h2>
                  {homeRatingsLoading ? (
                    <HomePosterRowSkeleton count={8} textLines={3} />
                  ) : homeRatings.length === 0 ? (
                    <p className="home-content-placeholder">Rate movies and series from Browse to see them here.</p>
                  ) : (
                    <div className="home-poster-row-with-arrows">
                      {homeRatings.length >= 9 && (
                        <button type="button" className="home-poster-arrow home-poster-arrow-left" onClick={() => scrollRatings("left")} aria-label="Scroll left">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
                        </button>
                      )}
                      <div className="home-poster-row home-poster-row--center" ref={homeRatingsRowRef}>
                        {homeRatings.map((item) => (
                          <a
                            key={`${item.type}-${item.tmdb_id}`}
                            href={`#/browse/${item.type}/${item.tmdb_id}`}
                            className="home-poster-card"
                            onClick={() => {
                              setCurrentPage("browse");
                              setBrowseDetail({ type: item.type, id: String(item.tmdb_id) });
                            }}
                          >
                            <div className="home-poster-card-poster">
                              {item.poster_path ? (
                                <img src={tmdbCardPosterUrl(item.poster_path)} alt={item.title || ""} loading="lazy" decoding="async" />
                              ) : (
                                <div className="home-poster-card-placeholder">🎬</div>
                              )}
                            </div>
                            <span className="home-poster-card-title">{item.title || "Untitled"}</span>
                            <span className="home-poster-card-subtitle">{item.type === "tv" ? "Series" : "Movie"}</span>
                            <span className="home-poster-card-rating">⭐ {item.rating}</span>
                          </a>
                        ))}
                      </div>
                      {homeRatings.length >= 9 && (
                        <button type="button" className="home-poster-arrow home-poster-arrow-right" onClick={() => scrollRatings("right")} aria-label="Scroll right">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
                        </button>
                      )}
                    </div>
                  )}
                  <div className="home-section-action-wrap">
                    <button
                      type="button"
                      className="home-section-action-btn"
                      onClick={() => {
                        setCurrentPage("ratings");
                        window.location.hash = "#/ratings";
                      }}
                    >
                      <span>View all ratings</span>
                      <svg className="home-section-action-btn-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="home-content-section home-library-section">
                  <h2 className="home-content-section-title">Your watchlist</h2>
                  {homeWatchlistLoading ? (
                    <HomePosterRowSkeleton count={8} textLines={3} />
                  ) : homeWatchlist.length === 0 ? (
                    <p className="home-content-placeholder">Your watchlist will appear here. Add titles from Browse.</p>
                  ) : (
                    <div className="home-poster-row-with-arrows">
                      {homeWatchlist.length >= 9 && (
                        <button type="button" className="home-poster-arrow home-poster-arrow-left" onClick={() => scrollWatchlist("left")} aria-label="Scroll left">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
                        </button>
                      )}
                      <div className="home-poster-row home-poster-row--center" ref={homeWatchlistRowRef}>
                        {homeWatchlist.map((item) => {
                          return (
                          <a
                            key={`${item.type}-${item.tmdb_id}`}
                            href={`#/browse/${item.type}/${item.tmdb_id}`}
                            className="home-poster-card"
                            onClick={() => {
                              setCurrentPage("browse");
                              setBrowseDetail({ type: item.type, id: String(item.tmdb_id) });
                            }}
                          >
                            <div className="home-poster-card-poster">
                              {item.poster_path ? (
                                <img src={tmdbCardPosterUrl(item.poster_path)} alt={item.title || ""} loading="lazy" decoding="async" />
                              ) : (
                                <div className="home-poster-card-placeholder">🎬</div>
                              )}
                            </div>
                            <span className="home-poster-card-title">{item.title || "Untitled"}</span>
                            <span className="home-poster-card-subtitle">{item.type === "tv" ? "Series" : "Movie"}</span>
                          </a>
                          );
                        })}
                      </div>
                      {homeWatchlist.length >= 9 && (
                        <button type="button" className="home-poster-arrow home-poster-arrow-right" onClick={() => scrollWatchlist("right")} aria-label="Scroll right">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
                        </button>
                      )}
                    </div>
                  )}
                  <div className="home-section-action-wrap">
                    <button
                      type="button"
                      className="home-section-action-btn"
                      onClick={() => {
                        setCurrentPage("watchlist");
                        window.location.hash = "#/watchlist";
                      }}
                    >
                      <span>View full watchlist</span>
                      <svg className="home-section-action-btn-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {currentPage === "browse" && (
          <Suspense fallback={<RouteFallback />}>
            {browseDetail ? (
              <MediaDetail
                mediaType={browseDetail.type}
                tmdbId={browseDetail.id}
                user={user}
                backLabel={detailBackButtonLabel()}
                onBack={() => {
                  const t = readBrowseDetailReturn();
                  setBrowseDetail(null);
                  window.location.hash = hashPathForLocation(t.hash);
                }}
              />
            ) : (
              <Browse
                key={`browse-${browseMenuCategory || "default"}`}
                initialSearchQuery={browseSearchQuery}
              />
            )}
          </Suspense>
        )}

        {currentPage === "top100" && (
          <Suspense fallback={<RouteFallback />}>
            <Top100
              mediaType={top100Tab}
              user={user}
              onMediaTypeChange={(t) => {
                setTop100Tab(t);
                window.location.hash = t === "tv" ? "#/top100/tv" : "#/top100/movie";
              }}
            />
          </Suspense>
        )}

        {currentPage === "profile" && user && (
          <Suspense fallback={<RouteFallback />}>
            <UserProfile user={user} navigateToPage={navigateFromProfile} />
          </Suspense>
        )}

        {currentPage === "user" && viewedUserId && (
          <Suspense fallback={<RouteFallback />}>
            <UserProfile user={user} viewedUserId={viewedUserId} navigateToPage={navigateFromProfile} />
          </Suspense>
        )}

        {currentPage === "ratings" && user && (
          <Suspense fallback={<RouteFallback />}>
            <Ratings user={user} />
          </Suspense>
        )}

        {currentPage === "favorites" && user && (
          <Suspense fallback={<RouteFallback />}>
            <Favorites user={user} />
          </Suspense>
        )}

        {currentPage === "watchlist" && user && (
          <Suspense fallback={<RouteFallback />}>
            <Watchlist user={user} />
          </Suspense>
        )}

        {currentPage === "suggestions" && (
          <div className="profile-section">
            <div className="profile-container profile-container--page suggestions-all-page">
              <div className="profile-header">
                <button
                  type="button"
                  className="media-detail-back"
                  onClick={() => {
                    setCurrentPage("home");
                    window.location.hash = "";
                  }}
                >
                  ← Back to Home
                </button>
                <h1 className="profile-title">Suggestions for you</h1>
                {homeSuggestionsGenreName ? (
                  <p className="ratings-subtitle">
                    Up to 20 movies in <strong>{homeSuggestionsGenreName}</strong>, based on your ratings
                  </p>
                ) : (
                  <p className="ratings-subtitle">Up to 20 titles picked from your taste</p>
                )}
              </div>
              {!user ? (
                <p className="browse-placeholder-text">Sign in to see personalized suggestions.</p>
              ) : homeSuggestionsLoading && homeSuggestions.length === 0 ? (
                <div className="profile-placeholder profile-placeholder--skeleton">
                  <BrowseGridSkeleton count={12} />
                </div>
              ) : homeSuggestions.length === 0 ? (
                <p className="browse-placeholder-text">
                  Rate more titles to get suggestions based on your favorite genre.
                </p>
              ) : (
                <div className="browse-grid browse-grid-compact suggestions-all-grid">
                  {homeSuggestions.map((item) => (
                    <a
                      key={`${item.mediaType}-${item.id}`}
                      href={`#/browse/${item.mediaType}/${item.id}`}
                      className="browse-card browse-card-clickable"
                      title={item.title}
                      onClick={() => {
                        setCurrentPage("browse");
                        setBrowseDetail({ type: item.mediaType, id: String(item.id) });
                      }}
                    >
                      <div className="browse-card-poster">
                        {item.posterPath ? (
                          <img
                            className="browse-card-poster-image"
                            src={tmdbCardPosterUrl(item.posterPath)}
                            alt=""
                            loading="lazy"
                            decoding="async"
                          />
                        ) : (
                          <div className="browse-card-poster-placeholder">🎬</div>
                        )}
                      </div>
                      <div className="browse-card-body">
                        <div className="browse-card-title">{item.title}</div>
                        <div className="browse-card-meta">
                          <span className="browse-card-type">{item.mediaType === "tv" ? "Series" : "Movie"}</span>
                          {item.year ? <span className="browse-card-year">{item.year}</span> : null}
                          {item.rating != null ? (
                            <span className="browse-card-rating">⭐ {item.rating.toFixed(1)}</span>
                          ) : null}
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {currentPage === "lists" && (
          <Suspense fallback={<RouteFallback />}>
            <Lists user={user} />
          </Suspense>
        )}

        {currentPage === "notifications" && user && (
          <Suspense fallback={<RouteFallback />}>
            <Notifications
              user={user}
              onMarkRead={handleNotificationsMarkRead}
            />
          </Suspense>
        )}

        {currentPage === "settings" && user && (
          <Suspense fallback={<RouteFallback />}>
            <Settings user={user} />
          </Suspense>
        )}

        {currentPage === "resetPassword" && (
          <ResetPassword
            user={user}
            onDone={() => {
              passwordRecoveryUiLockRef.current = false;
              window.location.hash = "";
              setCurrentPage("home");
            }}
          />
        )}

        {currentPage === "admin" && user && (
          <Suspense fallback={<RouteFallback />}>
            <AdminPanel user={user} isAdmin={userIsAdmin} />
          </Suspense>
        )}

        {currentPage === "community" && (
          <Suspense fallback={<RouteFallback />}>
            {selectedPostId ? (
              <PostDetail
                postId={selectedPostId}
                focusCommentId={postFocusCommentId}
                user={user}
                isAdmin={userIsAdmin}
                onBack={() => {
                  setSelectedPostId(null);
                  setPostFocusCommentId(null);
                  setCurrentPage("community");
                  window.location.hash = "/community";
                }}
              />
            ) : (
              <Community
                user={user}
                onPostClick={(postId) => {
                  setSelectedPostId(postId);
                  window.location.hash = `/post/${postId}`;
                }}
              />
            )}
          </Suspense>
        )}
      </main>

      {showAuthModal && (
        <AuthModal 
          onClose={() => setShowAuthModal(false)}
          onLoginSuccess={() => {
            setShowAuthModal(false);
            setCurrentPage("home");
            window.location.hash = "";
          }}
          onSignupSuccess={() => {
            setShowAuthModal(false);
            setCurrentPage("profile");
            window.location.hash = "#/profile";
          }}
        />
      )}

      <footer className="footer">
        <p className="footer-text">© 2026 RateZone</p>
      </footer>
    </div>
  );
}

export default App;
